import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useState } from "react";

import SelectDataset from "@/components/SelectDataset";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ToastType } from "@/enums";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { Entity, EntityType, useCreateDatasetGroup, useDatasetGroup } from "@/queries";
import { displayToast } from "@/utils/toast";

interface SelectOrCreateDatasetDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called once a dataset group has been picked or created. Receives the dataset
   * group id. Should return a boolean indicating whether the dialog can close
   * (true = success, false = keep open so the user can retry).
   */
  onConfirm: (datasetGroupId: number) => Promise<boolean> | boolean;
  userToken: string;
  title?: ReactNode;
  description?: ReactNode;
  submitLabel?: string;
  submitNewLabel?: string;
  /**
   * Optional dataset group id to preselect. When provided, the create-new
   * input is hidden so the dialog acts as a confirmation step against an
   * already-chosen dataset.
   */
  preselectDatasetGroupId?: number;
  /** Called after a successful confirm + close. */
  onConfirmSuccess?: () => void;
}

const SelectOrCreateDatasetDialog = observer(
  ({
    isOpen,
    onOpenChange,
    onConfirm,
    userToken,
    title = "Add to Dataset",
    description = "Pick an existing dataset or create a new one.",
    submitLabel = "Add",
    submitNewLabel = "Create & Add",
    preselectDatasetGroupId,
    onConfirmSuccess,
  }: SelectOrCreateDatasetDialogProps) => {
    const activeWorkspace = useActiveWorkspace();

    const [selectedDataset, setSelectedDataset] = useState<
      Entity<EntityType.DATASET> | undefined
    >();
    const [newDatasetName, setNewDatasetName] = useState("");
    const [hasInitializedPreselect, setHasInitializedPreselect] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const preselectQuery = useDatasetGroup(
      userToken,
      preselectDatasetGroupId ? String(preselectDatasetGroupId) : "",
    );

    useEffect(() => {
      if (!preselectDatasetGroupId || hasInitializedPreselect) {
        return;
      }
      const datasetGroup = preselectQuery.data?.dataset_group;
      if (!datasetGroup) {
        return;
      }
      setSelectedDataset({
        id: preselectDatasetGroupId,
        name: datasetGroup.name,
        type: EntityType.DATASET,
        path: [],
      });
      setHasInitializedPreselect(true);
    }, [preselectDatasetGroupId, preselectQuery.data, hasInitializedPreselect]);

    useEffect(() => {
      if (!isOpen) {
        setSelectedDataset(undefined);
        setNewDatasetName("");
        setHasInitializedPreselect(false);
      }
    }, [isOpen]);

    const { mutateAsync: createDatasetGroup, isLoading: isCreating } =
      useCreateDatasetGroup(userToken);

    const trimmedNewName = newDatasetName.trim();
    const isCreatingNew = trimmedNewName.length > 0;
    const hasPrefilledDataset = preselectDatasetGroupId !== undefined;
    const canSubmit = isCreatingNew ? !!activeWorkspace : !!selectedDataset;
    const isLoading = isSubmitting || isCreating;

    const handleSelectDataset = (dataset: Entity<EntityType.DATASET>) => {
      setSelectedDataset(dataset);
      setNewDatasetName("");
    };

    const handleNewNameChange = (value: string) => {
      setNewDatasetName(value);
      if (value.trim().length > 0) {
        setSelectedDataset(undefined);
      }
    };

    const resolveDatasetGroupId = async (): Promise<number | null> => {
      if (isCreatingNew) {
        if (!activeWorkspace) {
          displayToast("No active workspace", ToastType.error);
          return null;
        }
        try {
          const created = await createDatasetGroup({
            name: trimmedNewName,
            workspace_id: activeWorkspace.id,
            folder_id: null,
          });
          const newId = created?.dataset_group?.id;
          if (!created?.success || !newId) {
            displayToast(created?.message || "Could not create dataset", ToastType.error);
            return null;
          }
          return newId;
        } catch {
          displayToast("Could not create dataset", ToastType.error);
          return null;
        }
      }
      if (selectedDataset?.id === null || selectedDataset?.id === undefined) {
        return null;
      }
      const datasetId = Number(selectedDataset.id);
      return Number.isFinite(datasetId) ? datasetId : null;
    };

    const handleConfirm = async () => {
      if (isLoading) {
        return;
      }
      setIsSubmitting(true);
      try {
        const datasetGroupId = await resolveDatasetGroupId();
        if (!datasetGroupId) {
          return;
        }
        const success = await onConfirm(datasetGroupId);
        if (success) {
          onOpenChange(false);
          onConfirmSuccess?.();
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog onOpenChange={onOpenChange} open={isOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            {description && <p className="text-sm text-gray-600">{description}</p>}
            <SelectDataset
              dataset={selectedDataset}
              onSelect={handleSelectDataset}
              readonly={isCreatingNew || hasPrefilledDataset}
              selectLatest
            />
            {!hasPrefilledDataset && (
              <>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span>or create new</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                <Input
                  placeholder="New dataset name"
                  value={newDatasetName}
                  onChange={e => handleNewNameChange(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && canSubmit && !isLoading) {
                      handleConfirm();
                    }
                  }}
                />
              </>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} isLoading={isLoading} disabled={!canSubmit}>
              {isCreatingNew ? submitNewLabel : submitLabel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  },
);

export default SelectOrCreateDatasetDialog;
